/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { DownloadType, ScopeTotals } from '../../types/download';
import { BasicAlert } from '../alert/BasicAlert';
import { DownloadQuestionSettings } from './DownloadQuestionSettings';
import { DownloadSettingPropType, generatingReportText } from './DownloadSettingModal';
import { DownloadSettingOptions } from './DownloadSettingOptions';
import { PackSelect } from './partials/PackSelect';
import { Totals } from './partials/Totals';
import { getDownLoadOptions } from './util/downloadReportHandler';
import { PackOption } from '../../constants/standards-frameworks';
import { useAppSelector } from '../../reducers';
import { isStaff } from '../../selectors/user';

type DownloadOthersSettingPropType = Pick<
  DownloadSettingPropType,
  'isOpen' | 'isDisabled' | 'title' | 'downloadSettings' | 'disabledOptions' | 'handleClose' | 'handleChange' | 'onDownload' | 'downloadDoc'
> & { getTotals?: (type: string) => ScopeTotals, packTitle?: string, defaultPackCode?: string, packOptions: PackOption[]; };

export const DownloadOthersSettingModal = (props: DownloadOthersSettingPropType) => {
  const {
    isOpen,
    isDisabled = false,
    title,
    downloadSettings,
    disabledOptions = ['word'],
    handleClose,
    handleChange,
    onDownload,
    downloadDoc = () => undefined,
    getTotals,
    packTitle,
    defaultPackCode,
    packOptions
  } = props;

  const [isGeneratingReport, setGeneratingReport] = useState(false);
  const [packCode, setPackCode] = useState<string>('');
  const totals = getTotals?.(packCode);
  const userIsStaff = useAppSelector(isStaff);

  useEffect(() => {
    if (defaultPackCode) {
      setPackCode(defaultPackCode);
    }
  }, [defaultPackCode, isOpen]);

  const handleDownloadReport = async (downloadType: DownloadType, config?: { debug?: boolean }) => {
    setGeneratingReport(true);
    await onDownload(downloadType, { ...config, packCode });
    handleToggle();
    setGeneratingReport(false);
  };

  const handleDownloadDoc = async () => {
    setGeneratingReport(true);
    await downloadDoc();
    handleClose();
    setGeneratingReport(false);
  }

  const handleToggle = () => {
    handleClose();
    setPackCode('');
  }

  const options = getDownLoadOptions({
    code: packCode,
    disabledOptions,
    handleDownloadReport,
    handleDownloadDoc,
    isStaff: userIsStaff
  });

  return (
    <Modal className='download-setting__modal' isOpen={isOpen} toggle={handleToggle} backdrop='static'>
      <ModalHeader toggle={handleToggle}>{title}</ModalHeader>
      <ModalBody>
        {isGeneratingReport ? (
          <BasicAlert type='info'>{generatingReportText}</BasicAlert>
        ) : (
          <>
            {packTitle ? <h5>{packTitle}</h5> : null}
            <div className='mb-3'>
              <PackSelect pack={packCode} packOptions={packOptions} handleSelectPack={(pack: string) => setPackCode(pack)} />
              <Totals totals={totals} className='py-2' />
            </div>
            <DownloadQuestionSettings
              code={packCode}
              isDisabled={!packCode}
              totals={totals}
              downloadSettings={downloadSettings}
              handleChange={handleChange}
            />
            <DownloadSettingOptions
              options={options}
              isDisabled={isDisabled || !packCode}
            />
          </>
        )}
      </ModalBody>
      <ModalFooter className='text-right'>
        <Button outline size='sm' className='mr-2 pl-4 pr-4' onClick={handleToggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
